import { z } from "zod"

export const messageCatalogMetadata = {
  activityKey: "message",
  label: "Chat",
  emoji: "💬",
  sortOrder: 0,
} as const

export const activityTrackKeySchema = z.literal(
  messageCatalogMetadata.activityKey,
)
export type ActivityTrackKey = z.infer<typeof activityTrackKeySchema>

export const commonTrackConfigurationSchema = z
  .object({
    xpMin: z.number().int().min(0),
    xpMax: z.number().int().min(0),
    cooldownSeconds: z.number().int().min(0),
    dailyXpCap: z.number().int().min(0),
    isEnabled: z.boolean(),
    rewardPolicy: z.enum(["stack", "replace"]),
    levelUpChannelId: z.string().trim().min(1).nullable(),
  })
  .strict()

const multiplierSchema = z.number().finite().positive().max(3)
const uniqueIds = (ids: string[], ctx: z.RefinementCtx) => {
  const seen = new Set<string>()

  for (const id of ids) {
    if (seen.has(id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "IDs must be unique",
      })
      return
    }
    seen.add(id)
  }
}

export const messageTrackSettingsSchema = z
  .object({
    allowedChannelIds: z.array(z.string().trim().min(1)),
    noXpRoleIds: z.array(z.string().trim().min(1)),
    minMessageLength: z.number().int().min(1).max(2000),
    channelXpMultipliers: z.record(z.string().trim().min(1), multiplierSchema),
    roleXpMultipliers: z.record(z.string().trim().min(1), multiplierSchema),
    lengthEffort: z
      .object({
        charactersPerStep: z.number().int().min(1).max(1000),
        xpMultiplierPerStep: z.number().finite().min(0).max(1),
        maxSteps: z.number().int().min(0).max(10),
      })
      .strict(),
  })
  .strict()
  .superRefine((settings, ctx) => {
    uniqueIds(settings.allowedChannelIds, ctx)
    uniqueIds(settings.noXpRoleIds, ctx)
  })

export const messageTrackConfigurationSchema = commonTrackConfigurationSchema
  .extend({
    activityKey: z.literal(messageCatalogMetadata.activityKey),
    settings: messageTrackSettingsSchema,
  })
  .strict()

export const trackConfigurationSchema = messageTrackConfigurationSchema
export type TrackConfiguration = z.infer<typeof trackConfigurationSchema>
export type MessageTrackSettings = z.infer<typeof messageTrackSettingsSchema>

export const trackDefinitionSchema = trackConfigurationSchema
  .extend({
    label: z.literal(messageCatalogMetadata.label),
    emoji: z.literal(messageCatalogMetadata.emoji),
    sortOrder: z.literal(messageCatalogMetadata.sortOrder),
  })
  .strict()
export type TrackDefinition = z.infer<typeof trackDefinitionSchema>

export const MESSAGE_TRACK_DEFAULTS = trackConfigurationSchema.parse({
  activityKey: messageCatalogMetadata.activityKey,
  xpMin: 15,
  xpMax: 25,
  cooldownSeconds: 60,
  dailyXpCap: 0,
  isEnabled: true,
  rewardPolicy: "replace",
  levelUpChannelId: null,
  settings: {
    allowedChannelIds: [],
    noXpRoleIds: [],
    minMessageLength: 3,
    channelXpMultipliers: {},
    roleXpMultipliers: {},
    lengthEffort: {
      charactersPerStep: 100,
      xpMultiplierPerStep: 0,
      maxSteps: 0,
    },
  },
})
